import type { ComponentProps } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, UserRound } from "lucide-react";
import { AccountMenu } from "@marekl/adminer/components";
import { useBreakpoint } from "@marekl/adminer/hooks";
import { Topbar } from "@marekl/adminer/shell";
import { useAuth } from "../../auth/useAuth";
import { useLogout } from "../../auth/queries";
import { SeasonSwitcher } from "../../components/SeasonSwitcher";
import { NoticeStrip } from "../../components/Notices";
import { EnvironmentChip } from "../../components/EnvironmentBanner";

type TopbarProps = ComponentProps<typeof Topbar> & {
  // Which season control this page gets. OMIT IT — the default — on any
  // page whose data is not season-scoped: a picker that changes nothing
  // there does not merely waste space, it claims the page is showing the
  // season it names.
  //
  //   "readonly" — the page follows the selection but writes only into the
  //                active season, so a non-active choice is a read-only
  //                historical view (the team roster).
  //   "writable" — the page follows the selection and writes into it too
  //                (the competition calendar, built before its season goes
  //                active), so no read-only marker is shown.
  seasonSwitcher?: "readonly" | "writable";
};

/**
 * The account control, in the topbar rather than the sidebar footer.
 *
 * It lives here and not in `AppLayout` because the pages render the
 * `Topbar` themselves — see `AppTopbar` below.
 *
 * Two items only. Changing a password stays on `ProfilePage`, where the
 * rest of the account is: a menu that offers one specific edit and none
 * of the others invites the question of why that one.
 */
function Account() {
  const { user } = useAuth();
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();

  if (!user) return null;

  return (
    <AccountMenu
      user={{
        displayName: user.display_name || user.email,
        roleLabel: user.email,
      }}
      items={[
        {
          id: "profile",
          label: "Profil",
          icon: <UserRound aria-hidden="true" size={16} />,
          href: "/profile",
        },
        { type: "separator" },
        {
          id: "logout",
          label: "Odhlásit se",
          icon: <LogOut aria-hidden="true" size={16} />,
          danger: true,
          onSelect: () =>
            logout(undefined, {
              onSettled: () => navigate("/login", { replace: true }),
            }),
        },
      ]}
    />
  );
}

// AppTopbar wraps the lib Topbar: it injects the account control on every
// authenticated page, and the season switcher for the pages that ask.
// Every authenticated page uses this instead of importing Topbar directly.
export function AppTopbar({ crumbs, right, seasonSwitcher, ...rest }: TopbarProps) {
  // Crumbs are dropped on a phone. They are not merely cramped there — the
  // trail elides to „Týmy / …", so the one crumb naming the current page is
  // the one that disappears, leaving a parent link and an ellipsis in the
  // width the season switcher needs. The page's own H1 says where you are.
  //
  // Done here rather than in CSS on purpose: the theme layer deliberately
  // targets no `.adminer-*` class (see AGENTS.md §4), and `useBreakpoint`'s
  // "sm" is the same ≤520px this project documents as its phone breakpoint.
  const isPhone = useBreakpoint() === "sm";

  // Stopping an impersonation stays on ImpersonationBanner, deliberately.
  // The banner is on screen the whole time the mode is on, with the exit
  // beside the sentence that explains it; a second exit hidden behind a
  // click would be two ways out of a state most people enter twice a year.
  //
  // The notices' lišty follow the top bar, so they sit under it on every
  // page: each page renders its own top bar, and this is the one place all
  // of them pass through.
  return (
    <>
      <Topbar
        {...rest}
        crumbs={isPhone ? [] : crumbs}
        right={
          <>
            <EnvironmentChip />
            {seasonSwitcher && <SeasonSwitcher writable={seasonSwitcher === "writable"} />}
            {right}
            <Account />
          </>
        }
      />
      <NoticeStrip />
    </>
  );
}
